/**
 * Audio session ducking — stub for native media volume control.
 * Dips background media by 60 % while spoken calibration cues play.
 */

export type AudioSessionController = {
  /** Set background media volume to `factor` × baseline (0–1). */
  duckBackgroundMedia: (volumeFactor: number) => Promise<void> | void
  restoreBackgroundMedia: () => Promise<void> | void
}

const DUCK_VOLUME_FACTOR = 0.4

let registered: AudioSessionController | null = null
let duckDepth = 0

export function registerAudioSessionController(controller: AudioSessionController): void {
  registered = controller
}

export function unregisterAudioSessionController(): void {
  registered = null
  duckDepth = 0
}

/** Reduce background media to 40 % of baseline (−60 %). */
export async function beginSpokenCue(): Promise<void> {
  duckDepth += 1
  if (duckDepth === 1) {
    await registered?.duckBackgroundMedia(DUCK_VOLUME_FACTOR)
  }
}

export async function endSpokenCue(): Promise<void> {
  duckDepth = Math.max(0, duckDepth - 1)
  if (duckDepth === 0) {
    await registered?.restoreBackgroundMedia()
  }
}

export async function speakCalibrationCue(text: string): Promise<void> {
  await beginSpokenCue()
  try {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      await new Promise<void>((resolve) => {
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.rate = 0.95
        utterance.pitch = 1
        utterance.onend = () => resolve()
        utterance.onerror = () => resolve()
        window.speechSynthesis.speak(utterance)
      })
    }
  } finally {
    await endSpokenCue()
  }
}

/** No-op controller for web preview when native bridge is absent. */
export const noopAudioSessionController: AudioSessionController = {
  duckBackgroundMedia: () => undefined,
  restoreBackgroundMedia: () => undefined,
}
